// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Fairness Verifier
// Paste any spin hash → verify it was provably fair
// ================================================================
import React, { useState } from 'react';
import ProvablyFairEngine from '../engine/ProvablyFairEngine';
import { useCasino } from '../context/CasinoShell';

export default function FairnessVerifier() {
  const { fairnessLog } = useCasino();
  const [serverSeed, setServerSeed] = useState('');
  const [clientSeed, setClientSeed] = useState('');
  const [nonce, setNonce] = useState('');
  const [commitmentHash, setCommitmentHash] = useState('');
  const [resultHash, setResultHash] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const handleVerify = async () => {
    setVerifying(true);
    setVerifyResult(null);

    try {
      const result = await ProvablyFairEngine.verify({
        serverSeed,
        clientSeed,
        nonce: parseInt(nonce),
        commitmentHash,
        resultHash,
      });
      setVerifyResult(result);
    } catch (err) {
      setVerifyResult({ valid: false, reason: err.message });
    }

    setVerifying(false);
  };

  const loadFromLog = (entry) => {
    setServerSeed(entry.serverSeed || '');
    setClientSeed(entry.clientSeed || '');
    setNonce(String(entry.nonce || ''));
    setCommitmentHash(entry.commitmentHash || '');
    setResultHash(entry.resultHash || '');
    setVerifyResult(null);
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>✅ FAIRNESS VERIFIER</h1>
      <p style={styles.subtitle}>
        Verify any spin was provably fair. Paste the proof data below — anyone can verify.
      </p>

      {/* How It Works */}
      <div style={styles.howItWorks}>
        <h3 style={styles.howTitle}>HOW PROVABLY FAIR WORKS</h3>
        <div style={styles.steps}>
          <div style={styles.step}>
            <span style={styles.stepNum}>1</span>
            <span style={styles.stepText}>Server generates a secret seed + nonce</span>
          </div>
          <div style={styles.step}>
            <span style={styles.stepNum}>2</span>
            <span style={styles.stepText}>SHA-256 hash of seed+nonce shown to you BEFORE spin</span>
          </div>
          <div style={styles.step}>
            <span style={styles.stepNum}>3</span>
            <span style={styles.stepText}>Your browser adds a random client seed</span>
          </div>
          <div style={styles.step}>
            <span style={styles.stepNum}>4</span>
            <span style={styles.stepText}>Result = SHA-256(server + client + nonce)</span>
          </div>
          <div style={styles.step}>
            <span style={styles.stepNum}>5</span>
            <span style={styles.stepText}>Server seed revealed — you verify the hash matches</span>
          </div>
        </div>
        <p style={styles.howNote}>
          If the commitment hash doesn't match, the server changed the seed after you saw it.
          If it matches, the spin was mathematically provably fair.
        </p>
      </div>

      {/* Manual Verification */}
      <div style={styles.verifyCard}>
        <h3 style={styles.cardTitle}>VERIFY A SPIN</h3>

        <div style={styles.inputGroup}>
          <label style={styles.label}>SERVER SEED</label>
          <input style={styles.input} value={serverSeed} onChange={e => setServerSeed(e.target.value)} placeholder="64-character hex string" />
        </div>
        <div style={styles.inputGroup}>
          <label style={styles.label}>CLIENT SEED</label>
          <input style={styles.input} value={clientSeed} onChange={e => setClientSeed(e.target.value)} placeholder="32-character hex string" />
        </div>
        <div style={styles.inputGroup}>
          <label style={styles.label}>NONCE</label>
          <input style={styles.input} type="number" value={nonce} onChange={e => setNonce(e.target.value)} placeholder="Spin number" />
        </div>
        <div style={styles.inputGroup}>
          <label style={styles.label}>COMMITMENT HASH (shown before spin)</label>
          <input style={styles.input} value={commitmentHash} onChange={e => setCommitmentHash(e.target.value)} placeholder="SHA-256 hash" />
        </div>
        <div style={styles.inputGroup}>
          <label style={styles.label}>RESULT HASH</label>
          <input style={styles.input} value={resultHash} onChange={e => setResultHash(e.target.value)} placeholder="SHA-256 hash" />
        </div>

        <button style={styles.verifyBtn} onClick={handleVerify} disabled={verifying}>
          {verifying ? 'VERIFYING...' : '🔍 VERIFY FAIRNESS'}
        </button>

        {/* Result */}
        {verifyResult && (
          <div style={{
            ...styles.result,
            background: verifyResult.valid ? '#00FF8822' : '#DC262622',
            borderColor: verifyResult.valid ? '#00FF8866' : '#DC262666',
          }}>
            <span style={{ fontSize: 32 }}>{verifyResult.valid ? '✅' : '❌'}</span>
            <h4 style={{ color: verifyResult.valid ? '#00FF88' : '#DC2626', margin: '8px 0', fontSize: 14, fontWeight: 900 }}>
              {verifyResult.valid ? 'VERIFIED FAIR' : 'VERIFICATION FAILED'}
            </h4>
            <p style={{ color: '#aaa', fontSize: 11 }}>{verifyResult.reason}</p>
          </div>
        )}
      </div>

      {/* Recent Spin Log */}
      {fairnessLog.length > 0 && (
        <div style={styles.logSection}>
          <h3 style={styles.cardTitle}>YOUR RECENT SPINS</h3>
          <p style={styles.logNote}>Click any spin to auto-fill verification fields</p>
          {fairnessLog.slice(-10).reverse().map((entry, i) => (
            <button key={i} style={styles.logRow} onClick={() => loadFromLog(entry)}>
              <div style={styles.logLeft}>
                <span style={styles.logGame}>{entry.gameId}</span>
                <span style={styles.logTime}>{new Date(entry.timestamp).toLocaleString()}</span>
              </div>
              <div style={styles.logRight}>
                <span style={styles.logBet}>Bet: ${entry.betAmount?.toFixed(2)}</span>
                <span style={{ ...styles.logWin, color: entry.winAmount > 0 ? '#00FF88' : '#666' }}>
                  Win: ${entry.winAmount?.toFixed(2)}
                </span>
              </div>
              <span style={styles.logHash}>{entry.resultHash?.slice(0, 12)}...</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: 16, maxWidth: 600, margin: '0 auto', minHeight: '100vh', background: '#0a0a0a' },
  title: { fontSize: 20, fontWeight: 900, color: '#fff', letterSpacing: 2, margin: 0 },
  subtitle: { fontSize: 12, color: '#888', marginTop: 4, marginBottom: 20, lineHeight: 1.5 },
  howItWorks: {
    padding: 16, background: '#0a1a0a', borderRadius: 12, border: '1px solid #00FF8833', marginBottom: 20,
  },
  howTitle: { fontSize: 12, color: '#00FF88', fontWeight: 900, letterSpacing: 2, marginBottom: 12, textAlign: 'center' },
  steps: { display: 'flex', flexDirection: 'column', gap: 8 },
  step: { display: 'flex', alignItems: 'center', gap: 10 },
  stepNum: {
    width: 24, height: 24, borderRadius: '50%', background: '#00FF8833', color: '#00FF88',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, flexShrink: 0,
  },
  stepText: { fontSize: 11, color: '#ccc' },
  howNote: { fontSize: 10, color: '#666', marginTop: 12, lineHeight: 1.6, textAlign: 'center' },
  verifyCard: { padding: 16, background: '#111', borderRadius: 12, border: '1px solid #222', marginBottom: 20 },
  cardTitle: { fontSize: 12, color: '#DC2626', fontWeight: 900, letterSpacing: 2, marginBottom: 16 },
  inputGroup: { marginBottom: 12 },
  label: { display: 'block', fontSize: 8, color: '#666', fontWeight: 700, letterSpacing: 1, marginBottom: 4 },
  input: {
    width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #333',
    background: '#0a0a0a', color: '#fff', fontSize: 11, fontFamily: 'monospace', boxSizing: 'border-box',
  },
  verifyBtn: {
    width: '100%', padding: 12, borderRadius: 8, border: '2px solid #00FF88',
    background: '#00FF8833', color: '#00FF88', fontSize: 13, fontWeight: 900,
    letterSpacing: 2, cursor: 'pointer', marginTop: 8,
  },
  result: {
    padding: 16, borderRadius: 8, border: '1px solid', textAlign: 'center', marginTop: 16,
  },
  logSection: { background: '#111', borderRadius: 12, border: '1px solid #222', padding: 16 },
  logNote: { fontSize: 10, color: '#555', marginBottom: 12 },
  logRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px',
    borderBottom: '1px solid #1a1a1a', cursor: 'pointer', background: 'none', border: 'none',
    borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: '#1a1a1a', width: '100%', textAlign: 'left',
  },
  logLeft: { display: 'flex', flexDirection: 'column', gap: 2 },
  logGame: { fontSize: 10, color: '#DC2626', fontWeight: 700 },
  logTime: { fontSize: 8, color: '#555' },
  logRight: { display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'right' },
  logBet: { fontSize: 9, color: '#888' },
  logWin: { fontSize: 10, fontWeight: 700 },
  logHash: { fontSize: 8, color: '#333', fontFamily: 'monospace' },
};
